import { FaArrowLeft, FaDownload, FaList, FaSpinner } from "react-icons/fa";
import { Card, IconButton } from 'components/common';
import { useLocation, useNavigate } from "react-router-dom";
import logo from "assets/img/pebsteel.png"
import { Link } from "react-router-dom";
import { formatDate } from "helpers/utils";
import { CRM_CO_FORM_ISO, CRM_CO_TYPE_OF_CO } from "app_config";
import "assets/scss/print-form.scss"
import generatePDF, {Resolution} from 'react-to-pdf';
import React, { useState } from "react";
import PropTypes from 'prop-types';
import { toast } from "react-toastify";

// Plain-markup equivalents of the react-bootstrap components previously used
// here. This is a print form (print-form.scss + react-to-pdf) so the rendered
// markup/classes are kept identical to what react-bootstrap produced.
const CheckItem = ({ id, type = 'checkbox', label, checked, disabled, className = '', value }) => (
  <div className={`form-check ${className}`}>
    <input
      type={type}
      id={id}
      className="form-check-input"
      checked={checked || false}
      disabled={disabled}
      value={value}
      readOnly
    />
    {label && (
      <label className="form-check-label" htmlFor={id}>
        {label}
      </label>
    )}
  </div>
);

CheckItem.propTypes = {
  id: PropTypes.string,
  type: PropTypes.string,
  label: PropTypes.node,
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  value: PropTypes.string
};

const HeaderPrint = ({changeOrder, download}) => {
  const navigate = useNavigate()
  const {project} = changeOrder || {};

  const handleBackToProject = () => {
    navigate(`/crm/projects/${project?.id}`)
  }

  const handleDownloadPDF = () => {
    download()
  }

  return (
    <>
      <Card className="tw-mb-3" padding="none">
        <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3">
          <IconButton
            onClick={() => navigate(-1)}
            variant="ghost"
            size="sm"
            icon={<FaArrowLeft size={12} />}
          />
          <div className="tw-flex">
            <IconButton
              variant="ghost"
              size="sm"
              icon={<FaDownload size={12} />}
              iconAlign="middle"
              className="tw-hidden sm:tw-inline-flex tw-mr-1"
              onClick={handleDownloadPDF}
            >
              <span className="tw-hidden xl:tw-inline-block tw-ml-1">
                Download
              </span>
            </IconButton>

            <IconButton
              variant="ghost"
              size="sm"
              icon={<FaList size={12} />}
              iconAlign="middle"
              className="tw-hidden sm:tw-inline-flex tw-mr-1"
              onClick={handleBackToProject}
            >
              <span className="tw-hidden xl:tw-inline-block tw-ml-1">
                Back to Project
              </span>
            </IconButton>
          </div>
        </div>
      </Card>
    </>
  );
}

HeaderPrint.propTypes = {
  changeOrder: PropTypes.object.isRequired,
  download: PropTypes.func.isRequired
}

const ChangeOrderPrint = () => {

  const location = useLocation()
  const { changeOrder } = location.state
  const { project, building, change_order_form: co_form } = changeOrder;
  const [showExporting, setExporting] = useState(false)

  const descriptionChanges = Array.isArray(co_form?.description_of_changes) ? co_form.description_of_changes : []

  const options = {
    filename: `Change order ${building?.j_number} CO#${changeOrder?.co} ${building.name}.pdf`,
    // default is `save`
    //method: 'open',
    // default is Resolution.MEDIUM = 3, which should be enough, higher values
    // increases the image quality but also the size of the PDF, so be careful
    // using values higher than 10 when having multiple pages generated, it
    // might cause the page to crash or hang.
    resolution: Resolution.NORMAL,
    page: {
       // margin is in MM, default is Margin.NONE = 0
       margin: 20,
       // default is 'A4'
       format: 'A4',
       // default is 'portrait'
       orientation: 'portrait',
    },
    canvas: {
       // default is 'image/jpeg' for better size performance
       mimeType: 'application/pdf',
       qualityRatio: 1
    },
    // Customize any value passed to the jsPDF instance and html2canvas
    // function. You probably will not need this and things can break,
    // so use with caution.
    overrides: {
       // see https://artskydj.github.io/jsPDF/docs/jsPDF.html for more options
       pdf: {
          compress: true
       },
       // see https://html2canvas.hertzen.com/configuration for more options
       canvas: {
          useCORS: true
       }
    },
  }

  const getTargetElement = () => document.getElementById('change-order-form');

  const handleDownload = () => {
    setExporting(true)
    generatePDF(getTargetElement, options)
      .then(() => {
        setExporting(false);
      })
      .catch(error => {
        console.error('Error generating PDF:', error);
        toast.error('Error generating PDF:', error)
        setExporting(false)
      })
  }

  return (
    <>
      <Card className="tw-m-2" padding="none">
        <HeaderPrint changeOrder={changeOrder} download={handleDownload}/>

        {showExporting && (
          <div className="tw-flex tw-items-center tw-justify-center">
            <FaSpinner className="tw-animate-spin tw-mr-2" role="status" /> Downloading...
          </div>
        )}

        <div id="change-order-form"  className="m-4 print-form responsive-table text-1000">
          <table className="fs--1">
            <tr>
              <td colSpan="5" className="border text-center">
                <div className="fs-2 fw-bold mb-2">CHANGE ORDER INFORMATION SHEET</div>
              </td>
            </tr>
            <tr>
              <td className="border fw-semi-bold" width="15%">Job no.:</td>
              <td className="border" width="25%">
                <Link to={`/crm/projects/${project?.id}`}>{building?.j_number}</Link>
              </td>
              <td className="border text-center" rowSpan="3" width="20%">
                <img
                  className="img-fluid fit-cover rounded"
                  src={logo}
                  alt="Logo Pebsteel"
                  width="130"
                  height="120"
                />
              </td>
              <td className="border" width="15%">This Change Order No.:</td>
              <td className="border text-primary" width="25%">{changeOrder.co}</td>
            </tr>
            <tr>
              <td className="border fw-semi-bold">Customer</td>
              <td className="border text-primary">{project?.customer_name}</td>
              <td className="border">Date this C.O. Entered:</td>
              <td className="border text-primary">{formatDate(changeOrder.entry_date)}</td>
            </tr>
            <tr>
              <td className="border" colSpan="2"></td>
              <td className="border" colSpan="2">
                <div className="tw-flex tw-justify-between">
                  <div>
                    <span>Continuation Number Sheet Attached:</span>
                    <CheckItem checked={co_form?.continuation_sheet_attached} />
                  </div>
                  <div>
                    Number of sheet: <span className="text-primary">1</span>
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <td className="border fw-semi-bold">Type of C.O.</td>
              <td className="border" colSpan="4">
                <div className="tw-flex mt-1">
                  {CRM_CO_TYPE_OF_CO.map((option, index) => (
                    <CheckItem
                      key={option}
                      id={`${option}${index}`}
                      type="radio"
                      label={option}
                      value={option}
                      className="me-5"
                      checked={co_form?.type_of_co === option}
                    />
                  ))}
                </div>
              </td>
            </tr>
            <tr>
              <td className="border fw-semi-bold" rowSpan="3">Payment for this C.O.</td>
              <td className="border" colSpan="4">
                <div className="row mb-2">
                  <div className="col">
                    <div className="row mb-2">
                      <label className="col-form-label col-sm-5 text-lg-start" htmlFor="payment_of_price_co">
                        Price of this C.O.
                      </label>
                      <div className="col text-primary">
                        {co_form?.payment_of_price_co}
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="row gx-2">
                      <div className="col">
                        <CheckItem
                          id="to_be_established"
                          checked={co_form?.to_be_established}
                          label="To be established"
                        />
                      </div>
                      <div className="col">
                        <CheckItem
                          id="already_included_contract"
                          checked={co_form?.already_included_contract}
                          label="Already included contract"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <td className="border" colSpan="5">
                <div className="row mb-2">
                  <div className="col">
                    <div className="row mb-2">
                      <label className="col-form-label col-sm-5 text-lg-start" htmlFor="payment_of_mode">
                        Mode of Payment for this C.O.
                      </label>
                      <div className="col text-primary">
                        {co_form?.payment_of_mode}
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="row gx-2">
                      <div className="col">
                        <CheckItem
                          id="amend_lc"
                          checked={co_form?.amend_lc}
                          label="Amend L/C"
                        />
                      </div>
                      <div className="col">
                        <CheckItem
                          id="revise_contract"
                          checked={co_form?.revise_contract}
                          label="Revise contract value"
                        />
                      </div>
                      <div className="col">
                        <CheckItem
                          id="payment_others"
                          checked={co_form?.payment_others}
                          label="Other (specify)"
                        />
                        {co_form?.payment_others_specify && (
                          <div className="mt-0 ms-3 text-primary">{co_form?.payment_others_specify}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <td className="border" colSpan="5">
                <div className="row mb-2">
                  <div className="col">
                    <div className="row mb-2">
                      <label className="col-form-label col-sm-5 text-lg-start" htmlFor="customer_will_pay">
                        Customer will Pay
                      </label>
                      <div className="col text-primary">
                        {co_form?.customer_will_pay}
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="row gx-2">
                      <div className="col">
                        <CheckItem
                          id="fully"
                          checked={co_form?.fully}
                          label="Fully"
                        />
                      </div>
                      <div className="col">
                        <CheckItem
                          id="partially"
                          checked={co_form?.partially}
                          label="Partially"
                        />
                      </div>
                      <div className="col">
                        <CheckItem
                          id="no_charge"
                          checked={co_form?.no_charge}
                          label="No charge"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <td className="border fw-semi-bold">Engg. Documents Required after this C.O</td>
              <td className="border" colSpan="4">
                <div className="row mb-2">
                  <div className="col">
                    <CheckItem
                      id="desgin_calculation"
                      checked={co_form?.desgin_calculation}
                      label="Desgin calculation"
                    />
                    <CheckItem
                      id="approval_dwgs"
                      checked={co_form?.approval_dwgs}
                      label="Approval dwgs"
                    />
                  </div>
                  <div className="col">
                    <CheckItem
                      id="re_approval_dwgs"
                      checked={co_form?.re_approval_dwgs}
                      label="Re approval dwgs"
                    />
                    <CheckItem
                      id="erection_dwgs"
                      checked={co_form?.erection_dwgs}
                      label="Erection dwgs"
                    />
                  </div>
                  <div className="col">
                    <CheckItem
                      id="effected_sheets_of_approval_dwgs"
                      checked={co_form?.effected_sheets_of_approval_dwgs}
                      label="Effected sheets of approval dwgs"
                    />
                    <CheckItem
                      id="effected_sheets_of_erection_dwgs"
                      checked={co_form?.effected_sheets_of_erection_dwgs}
                      label="Effected sheets of erection dwgs"
                    />
                    <CheckItem
                      id="record_dwgs"
                      checked={co_form?.record_dwgs}
                      label="Record dwgs"
                    />
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <td className="border fw-semi-bold">This C.O. affects.</td>
              <td className="border" colSpan="4">
                <div className="row mb-2">
                  <div className="col">
                    <CheckItem
                      id="co_affects_design"
                      checked={co_form?.co_affects_design}
                      label="Desgin"
                    />
                  </div>
                  <div className="col">
                    <CheckItem
                      id="co_affects_drafting"
                      checked={co_form?.co_affects_drafting}
                      label="Drafting"
                    />
                  </div>
                  <div className="col">
                    <CheckItem
                      id="co_affects_fabrication"
                      checked={co_form?.co_affects_fabrication}
                      label="Fabrication"
                    />
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <td className="border fw-semi-bold">Sales Discount</td>
              <td className="border" colSpan="4">
                <div className="row mb-2">
                  <label className="col-form-label col-sm-3 text-lg-start" htmlFor="sales_discount_before">
                    Before this C.O.
                  </label>
                  <div className="col text-primary">
                    {co_form?.sales_discount_before}
                  </div>
                </div>
                <div className="row mb-2">
                  <label className="col-form-label col-sm-3 text-lg-start" htmlFor="sales_discount_after">
                    After this C.O
                  </label>
                  <div className="col text-primary">
                    {co_form?.sales_discount_after}
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <td className="border fw-semi-bold">Impact of Changes</td>
              <td className="border" colSpan="4">
                {[
                  'in STD Ex-works (US$)',
                  'in CFR (US$)',
                  'in S.B.O. (US$)',
                  'in Delivery Period (Weeks)',
                  'in Freight (US$)',
                  'in Erection (US$)',
                  'in Total Contract (US$)',
                  'in Erection Period (Weeks)',
                ].map((impactLabel) => (
                  <div className="row gy-0" key={impactLabel}>
                    <div className="col">
                      <CheckItem
                        label="Increase"
                        disabled
                      />
                    </div>
                    <div className="col">
                      <CheckItem
                        label="Decrease"
                        disabled
                      />
                    </div>
                    <div className="col">
                      {impactLabel}
                    </div>
                  </div>
                ))}
              </td>
            </tr>
            <tr>
              <td className="border fw-semi-bold">Contract Update (All Prices are in US$)</td>
              <td className="border" colSpan="4">
                <table className="table table-striped">
                  <thead className="bg-200">
                    <tr>
                      <th className="border">C.O#</th>
                      <th className="border">Date</th>
                      <th className="border">Std. Ex-Works</th>
                      <th className="border">Freight</th>
                      <th className="border">CFR/CIF Job Site</th>
                      <th className="border">SBO</th>
                      <th className="border">Erection</th>
                      <th className="border">Others</th>
                      <th className="border">Total Contract</th>
                    </tr>
                  </thead>
                </table>

                <div className="tw-flex tw-justify-around">
                  <div className="fw-semi-bold">
                    New Delivery Period (Weeks): <span className="text-primary mx-3">{co_form?.new_delivery_period_weeks}</span>
                  </div>
                  <div className="fw-semi-bold">
                    New Erection Period (Weeks): <span className="text-primary mx-3">{co_form?.new_erection_period_weeks}</span>
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <td className="border fw-semi-bold">Description of changes</td>
              <td className="border" colSpan="4">
                <table className="table table-striped">
                  <thead className="bg-200">
                    <tr>
                      <th className="border" width="10%">Bldg. No.</th>
                      <th className="border" width="10%">Change No.</th>
                      <th className="border">Description of changes</th>
                    </tr>
                  </thead>
                  <tbody className="text-1000">
                    {descriptionChanges && descriptionChanges.map((item) => (
                      <tr key={item.id}>
                        <td className="border">{item.building_name}</td>
                        <td className="border">{item.change_no}</td>
                        <td className="border">
                          {/* Normalise pasted rich-text so it never overlaps: consistent
                              line-height + reset block margins (pasted <p> carry a tight
                              inline line-height / stray margins that overlap). */}
                          <div
                            className="tw-text-slate-700 [&_*]:[font-family:inherit] [&_*]:!tw-leading-normal [&_p]:tw-m-0 [&_p]:tw-mb-1 [&_p:last-child]:tw-mb-0 [&_pre]:tw-m-0 [&_pre]:tw-whitespace-pre-wrap [&_code]:tw-bg-transparent"
                            dangerouslySetInnerHTML={{ __html: item.description }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </td>
            </tr>
            <tr>
              <td className="border fw-semi-bold">Area office</td>
              <td className="border" colSpan="4">
                <div className="row gx-2 d-flex justify-content-around">
                  <div className="col-sm-4">
                    <div className="row mb-2">
                      <label className="col-form-label col-sm-auto text-lg-end">
                        Customer Service Engineer:
                      </label>
                      <div className="col text-primary mt-1">
                        {co_form?.cse_area}
                      </div>
                    </div>
                  </div>
                  <div className="col-sm-4">
                    <div className="row mb-2">
                      <label className="col-form-label col-sm-8 text-lg-end">
                        Customer Service Manager:
                      </label>
                      <div className="col text-primary mt-1">
                        {co_form?.csm_area}
                      </div>
                    </div>
                  </div>
                  <div className="col-sm-4">
                    <div className="row mb-2">
                      <label className="col-form-label col-sm-auto text-lg-end">
                        Date:
                      </label>
                      <div className="col text-primary mt-1">
                        {formatDate(co_form?.area_date)}
                      </div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <td className="border fw-semi-bold">Head office</td>
              <td className="border" colSpan="4">
                <div className="row gx-2 mb-2">
                  <div className="col-sm-4">
                    <div className="row mb-2">
                      <label className="col-form-label col-sm-auto text-lg-end">
                        Customer Service Engineer:
                      </label>
                      <div className="col text-primary mt-1">
                        {co_form?.cse_head}
                      </div>
                    </div>
                  </div>
                  <div className="col-sm-4">
                    <div className="row mb-2">
                      <label className="col-form-label col-sm-8 text-lg-end">
                        Supervisor:
                      </label>
                      <div className="col text-primary mt-1">
                        {co_form?.csm_head}
                      </div>
                    </div>
                  </div>
                  <div className="col-sm-4">
                    <div className="row mb-2">
                      <label className="col-form-label col-sm-auto text-lg-end">
                        Date:
                      </label>
                      <div className="col mt-1 text-primary">
                        {formatDate(co_form?.head_date)}
                      </div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </table>

          <div className="my-2 fs--1">{CRM_CO_FORM_ISO}</div>
        </div>
      </Card>
    </>
  );
};

export default ChangeOrderPrint;
